import { useEffect, useState } from "react";
import { FileSignature, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const inputCls = "w-full px-3 py-2 bg-white border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30";

export const ContractSigningGate = ({ onSigned }) => {
    const [contract, setContract] = useState(null);
    const [signature, setSignature] = useState("");
    const [title, setTitle] = useState("");
    const [agree, setAgree] = useState(false);
    const [baaAgree, setBaaAgree] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [loadError, setLoadError] = useState(false);

    const loadContract = () => {
        setLoadError(false);
        api.get("/institution/contract").then((r) => setContract(r.data)).catch(() => setLoadError(true));
    };

    useEffect(() => {
        loadContract();
    }, []);

    const needsBaa = contract && !contract.baa_signed_at && contract.baa;

    const sign = async () => {
        setError("");
        setSaving(true);
        try {
            await api.post("/institution/contract/sign", { signature_name: signature.trim(), title: title.trim() || null, baa_accepted: baaAgree });
            onSigned();
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not sign. Try again.");
        } finally { setSaving(false); }
    };

    if (!contract) {
        return (
            <div className="fixed inset-0 bg-phoenix-navy z-50 flex flex-col items-center justify-center gap-4">
                {loadError ? (
                    <div data-testid="contract-load-error" className="text-center space-y-3">
                        <p className="text-white text-sm">We couldn't load your service agreement. Please try again.</p>
                        <button data-testid="contract-load-retry" onClick={loadContract}
                            className="px-4 py-2 bg-white text-phoenix-navy text-sm font-semibold rounded-lg hover:bg-white/90">Retry</button>
                    </div>
                ) : (
                    <Loader2 className="w-7 h-7 animate-spin text-white" />
                )}
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-phoenix-navy/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div data-testid="contract-signing-gate" className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto p-6 sm:p-8 space-y-4">
                <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl bg-phoenix-navy text-white flex items-center justify-center"><FileSignature className="w-5 h-5" /></span>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-phoenix-orange">Activation required</div>
                        <h2 className="font-display font-bold text-xl text-phoenix-navy">Facility Service Agreement</h2>
                    </div>
                </div>
                <p className="text-sm text-muted-foreground">
                    Welcome, {contract.name}. Review and sign the service agreement and liability release to activate your facility ({contract.facility_id}, {contract.service_tier} tier).
                </p>

                <div>
                    <div className="text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-1.5">Contract terms</div>
                    <pre data-testid="contract-terms-text" className="bg-secondary/40 border border-border rounded-lg p-4 text-[11px] leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto font-mono">{contract.contract_terms || "—"}</pre>
                </div>
                <div>
                    <div className="text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-1.5">Liability release &amp; waiver</div>
                    <pre data-testid="liability-release-text" className="bg-secondary/40 border border-border rounded-lg p-4 text-[11px] leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto font-mono">{contract.contract_liability_release || "—"}</pre>
                </div>

                {needsBaa && (
                    <div>
                        <div className="text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-1.5">Business Associate Agreement (Exhibit B)</div>
                        <pre data-testid="baa-text" className="bg-secondary/40 border border-border rounded-lg p-4 text-[11px] leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto font-mono">{contract.baa}</pre>
                    </div>
                )}

                {error && <p data-testid="contract-sign-error" className="text-sm text-destructive">{error}</p>}
                <label className="flex items-start gap-2 text-sm text-phoenix-navy">
                    <input data-testid="contract-agree-checkbox" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 rounded" />
                    I am authorized to sign on behalf of this facility and agree to the contract terms and liability release above.
                </label>
                {needsBaa && (
                    <label className="flex items-start gap-2 text-sm text-phoenix-navy">
                        <input data-testid="baa-agree-checkbox" type="checkbox" checked={baaAgree} onChange={(e) => setBaaAgree(e.target.checked)} className="mt-0.5 rounded" />
                        I have read and agree to the Business Associate Agreement (Exhibit B) governing the handling of Protected Health Information (PHI).
                    </label>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                    <input data-testid="contract-signature-input" className={inputCls} placeholder="Full legal name (signature)" value={signature} onChange={(e) => setSignature(e.target.value)} />
                    <input data-testid="contract-title-input" className={inputCls} placeholder="Title (e.g. Warden) — optional" value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
                <button data-testid="contract-sign-submit" disabled={!agree || (needsBaa && !baaAgree) || signature.trim().length < 2 || saving} onClick={sign}
                    className="w-full px-4 py-3 bg-phoenix-navy text-white text-sm font-semibold rounded-xl hover:bg-phoenix-navy-2 disabled:opacity-50 transition-all">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Sign & activate facility"}
                </button>
            </div>
        </div>
    );
};
